import { useGlobalTheme } from './useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import RequiredLabelText from '../components/custom/RequiredLabelText';

/** Theme tokens for module settings pages (light dashboard vs dark glass). */
export function useModuleConfigTheme() {
  const { isDashboardLightMode: light } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const focusRing = qawaem ? 'focus:ring-[#6e9b6e]/45' : 'focus:ring-blue-500';
  const focusWithinRing = qawaem ? 'focus-within:ring-[#6e9b6e]/45' : 'focus-within:ring-blue-500';

  return {
    light,
    RequiredLabelText,
    ink: {
      primary: light ? '#0f172a' : '#ffffff',
      muted: light ? 'rgba(15, 23, 42, 0.62)' : 'rgba(255, 255, 255, 0.6)',
      hint: light ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.45)',
      subtle: light ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)',
      border: light ? 'rgba(15, 23, 42, 0.12)' : 'rgba(255, 255, 255, 0.2)',
    },
    card: light
      ? 'bg-white rounded-3xl p-6 border border-slate-200 shadow-sm'
      : 'bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20',
    cardSm: light
      ? 'bg-white rounded-xl p-6 border border-slate-200 shadow-sm'
      : 'bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20',
    title: light ? 'text-slate-900' : 'text-white',
    subtitle: light ? 'text-slate-600' : 'text-white/60',
    sectionTitle: light
      ? 'text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2'
      : 'text-lg font-semibold text-white mb-4 flex items-center gap-2',
    cardHeading: light
      ? 'text-xl font-bold text-slate-900 pb-2 border-b border-slate-200 mb-6 flex items-center gap-3'
      : 'text-xl font-bold text-white pb-2 border-b border-white/20 mb-6 flex items-center gap-3',
    label: light
      ? 'block w-full text-sm font-medium text-slate-800 mb-2 text-right'
      : 'block w-full text-sm font-medium text-white/80 mb-2 text-right',
    labelStyle: {
      display: 'block',
      width: '100%',
      textAlign: 'right',
      color: light ? '#0f172a' : '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '8px',
    },
    hint: light
      ? 'block mt-1.5 text-[11px] leading-snug italic text-slate-500'
      : 'block mt-1.5 text-[11px] leading-snug italic text-white/45',
    input: light
      ? `w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 ${focusRing} focus:border-transparent transition-all`
      : `w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 ${focusRing} focus:border-transparent transition-all`,
    helpText: light ? 'text-xs text-slate-500' : 'text-xs text-white/40',
    tabInactive: light
      ? 'bg-gradient-to-r from-[#3a624e] to-[#2e452e] text-white force-white hover:from-[#456b52] hover:to-[#355538] border border-[#2e452e]/30 shadow-sm'
      : 'bg-white/5 text-white force-white hover:text-white hover:bg-white/10',
    btnGhost: light
      ? 'flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl transition-all border border-slate-200'
      : 'flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all border border-white/20',
    daysInputRow: light
      ? `flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-2.5 focus-within:ring-2 ${focusWithinRing} focus-within:border-transparent transition-all`
      : `flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 focus-within:ring-2 ${focusWithinRing} focus-within:border-transparent transition-all`,
    daysInput: light
      ? 'min-w-0 flex-1 border-0 bg-transparent p-0 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
      : 'min-w-0 flex-1 border-0 bg-transparent p-0 text-white placeholder-white/50 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
    daysSuffix: light
      ? 'shrink-0 text-sm leading-none text-slate-500'
      : 'shrink-0 text-sm leading-none text-white/50',
    infoBox: light
      ? { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px' }
      : { backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '12px 14px' },
    infoText: light
      ? { color: '#475569', fontSize: '12px', lineHeight: 1.6, margin: 0 }
      : { color: 'rgba(255,255,255,0.75)', fontSize: '12px', lineHeight: 1.6, margin: 0 },
    accentText: qawaem
      ? light
        ? { color: '#5a855a', fontSize: '11px', marginTop: '8px', marginBottom: 0 }
        : { color: '#a8c4a8', fontSize: '11px', marginTop: '8px', marginBottom: 0 }
      : light
        ? { color: '#2563eb', fontSize: '11px', marginTop: '8px', marginBottom: 0 }
        : { color: '#93c5fd', fontSize: '11px', marginTop: '8px', marginBottom: 0 },
  };
}

export default useModuleConfigTheme;
